/** @jsxImportSource @emotion/react */
"use client";

import type { Interpolation } from "@emotion/react";
import { I18nMessage } from "@i18n/next";
import type { CommunityActivityFeedResultIntention } from "@packages/sdk";
import {
  communityActivityFeedReferenceTypeEnum,
  getFullName,
  useDate,
} from "@packages/sdk";
import type { ThemeType } from "@packages/ui";
import {
  Avatar,
  colors,
  gradientMapToV1,
  gradients,
  gradientsColorsEnd,
  gradientsColorsStart,
  ScrollArea,
  spacing,
  Text,
} from "@packages/ui";
import type { HTMLAttributes } from "react";
import { forwardRef } from "react";

import { RemovePostDropdownMenu } from "../../../RemovePostDropdownMenu";

const baseStyles: Interpolation = {
  display: "flex",
  flexDirection: "column",
  gap: spacing.ms,
};

const cardStyles: Interpolation = {
  height: spacing.full,
  left: spacing.none,
  position: "absolute",
  top: spacing.none,
  width: spacing.full,
};

const cardContainerStyles: Interpolation = {
  paddingBottom: spacing.full,
  position: "relative",
  width: spacing.full,
};

const headerStyles: Interpolation = {
  alignItems: "center",
  display: "flex",
  gap: spacing.s,
};

const headerTextStyles: Interpolation = {
  display: "flex",
  flex: 1,
  flexDirection: "column",
  gap: spacing.t,
  minWidth: spacing.t,
};

const headerTextTimeStyles: Interpolation<ThemeType> = (theme: ThemeType) => ({
  color: theme.semanticColors.neutralsMedium,
});

const scrollAreaStyles: Interpolation = {
  height: spacing.full,
};

const scrollAreaViewportStyles = (props: {
  gradient: number;
}): Interpolation => ({
  background: gradients[props.gradient],
  borderRadius: spacing.m,
  display: "flex",
  flexDirection: "column",
  padding: spacing.xl,
});

const scrollAreaMaskBottomStyles: Interpolation = {
  borderBottomLeftRadius: spacing.m,
  borderBottomRightRadius: spacing.m,
};

const scrollAreaMaskTopStyles: Interpolation = {
  borderTopLeftRadius: spacing.m,
  borderTopRightRadius: spacing.m,
};

const textStyles: Interpolation = {
  color: colors.white,
};

type ActivityCardIntentionProps = Omit<
  HTMLAttributes<HTMLDivElement>,
  "children"
> & {
  data: CommunityActivityFeedResultIntention;
};

export const ActivityCardIntention = forwardRef<
  HTMLDivElement,
  ActivityCardIntentionProps
>(({ data, ...props }, ref) => {
  const { getDateFormatDifferenceNow } = useDate();

  return (
    <div ref={ref} css={baseStyles} {...props}>
      <div css={headerStyles}>
        <Avatar imgSrc={data.user.image_url} size={40} />
        <div css={headerTextStyles}>
          <Text overflow="ellipsis" size="xl" type="detail">
            <strong>
              {getFullName({
                firstName: data?.user.name,
                lastName: data?.user.last_name,
              })}
            </strong>
            &nbsp;
            <I18nMessage message="posted_an_intention" />
          </Text>
          <Text size="l" css={headerTextTimeStyles} type="detail">
            {getDateFormatDifferenceNow({ date: new Date(data.created_at) })}
          </Text>
        </div>
        <RemovePostDropdownMenu
          communityPostId={data.id}
          type={communityActivityFeedReferenceTypeEnum.enum.intention}
        />
      </div>
      <div css={cardContainerStyles}>
        <div css={cardStyles}>
          <ScrollArea
            maskBottom
            maskBottomProps={{
              color:
                gradientsColorsEnd[
                  gradientMapToV1(data.reference.gradient_id ?? 0)
                ],
            }}
            maskBottomCSS={[scrollAreaMaskBottomStyles]}
            maskTop
            maskTopProps={{
              color:
                gradientsColorsStart[
                  gradientMapToV1(data.reference.gradient_id ?? 0)
                ],
            }}
            maskTopCSS={[scrollAreaMaskTopStyles]}
            css={scrollAreaStyles}
            tableMarginAuto
            viewportCSS={[
              scrollAreaViewportStyles({
                gradient: gradientMapToV1(data.reference.gradient_id ?? 0),
              }),
            ]}
          >
            <Text as="p" size="l" css={textStyles} type="body">
              {data.reference.text}
            </Text>
          </ScrollArea>
        </div>
      </div>
    </div>
  );
});

ActivityCardIntention.displayName = "ActivityCardIntention";
